import { Component, inject, Inject, Input, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommandBus, RootContext } from '@skylab-angular-shared/core';
import { ThemeSwitcherService, ThemeSwitcherType } from '@skylab-angular-shared/ui';
import { BannerClosedCommand, BannerInfoItem, BannerInfoKey, BannerInfoQuery } from '@skylab-shared/business';
import { LocalContext, RootContextBase, } from '@skylab-shared/core';
import { KsefConfigurationStatus } from '@skylab-shared/dataModel';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { KIMessageListenerEvent, KIMessageListenerService } from '../../ki/ki-message-listener.service';
import { KsefConfigurationService } from '../ksef-configuration/ksef-configuration.service';

@Component({
	selector: 'skl-banner',
	templateUrl: './banner.component.html'
})

export class BannerComponent {
	@Input() public key!: BannerInfoKey;

	private localContext!: LocalContext;
	public safeUrl!: SafeResourceUrl;
	public showBanner = signal(false);
	private themeSwitcherService = inject(ThemeSwitcherService);
	private kiMessageListenerService = inject(KIMessageListenerService);
	private ksefConfigurationService = inject(KsefConfigurationService);

	private messageListenerSubscription!: Subscription;
	private themeSwitcherSubscription!: Subscription;
	private ksefStatusSubscription!: Subscription;

	public constructor(
		@Inject(RootContext) rootContext: RootContextBase,
		private readonly commandBus: CommandBus,
		private sanitizer: DomSanitizer
	) {
		this.localContext = rootContext.createLocalContext();
	}

	public ngOnInit(): void {
		if (this.key === 'KSeF') {
			this.ksefStatusSubscription = this.ksefConfigurationService.ksefStatus$
				.pipe(
					distinctUntilChanged((prev, current) => prev.status === current.status)
				).subscribe(e => {
					this.messageListenerSubscription?.unsubscribe();
					this.themeSwitcherSubscription?.unsubscribe();
					if (e.status === KsefConfigurationStatus.INACTIVE) {
						this.initBanners();
					} else {
						this.showBanner.set(false);
					}
				})
		} else {
			this.initBanners();
		}
	}

	private initBanners(): void {
		this.messageListenerSubscription = this.kiMessageListenerService.messageListener.subscribe(async data => {
			if (data.type === 'KSEF_BANNER_EVENT') this.handleBannerEventsAsync(data.action);
		});

		this.themeSwitcherSubscription = this.themeSwitcherService.themeChange$.subscribe(e => {
			this.handleBannerChange(e);
		});

		this.handleBannerChange();
	}

	private async handleBannerEventsAsync(action: KIMessageListenerEvent['action']): Promise<void> {
		if (action === 'CLOSE') {
			await this.commandBus.commandAsync(new BannerClosedCommand({ key: 'KSeF' }));
			await this.localContext.refresh(BannerInfoQuery);
			this.handleBannerChange();
		} else if (action === 'KSEF_CONFIGURATION') {
			this.ksefConfigurationService.showKsefCreatorAsync();
		}
	}

	public ngOnDestroy(): void {
		this.localContext.dispose();
		this.messageListenerSubscription?.unsubscribe();
		this.themeSwitcherSubscription?.unsubscribe();
		this.ksefStatusSubscription?.unsubscribe();
	}

	private getBannerList(): BannerInfoItem[] {
		return this.localContext.getPreloaded(BannerInfoQuery) || [];
	}

	private handleBannerChange(e?: ThemeSwitcherType) {
		this.showBanner.set(false);
		this.safeUrl = '';
		const banner = this.getBannerList().find(b => b.key === this.key);
		if (banner) {
			setTimeout(() => {
				this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${banner.url}&theme=${e || this.themeSwitcherService.currentTheme}`);
				this.showBanner.set(true);
			})
		}
	}
}